import { BusinessDataTranslation } from "src/interfaces/menu.interface";
import { LocaleTranslationMap } from "src/types/locale.types";

export type { LocaleTranslationMap };

const EN_US = "en-US";
const ZH_CN = "zh-CN";

export function buildLocaleTranslationMap(
  translations: BusinessDataTranslation[] | undefined,
  enabledLocales: string[],
  fallback: {
    englishName?: string;
    nonEnglishName?: string;
    englishDescription?: string;
    nonEnglishDescription?: string;
  }
): LocaleTranslationMap {
  const map: LocaleTranslationMap = {};
  for (const locale of enabledLocales) {
    const row = translations?.find((entry) => entry.locale === locale);
    if (locale === EN_US) {
      map[locale] = {
        name: row?.name ?? fallback.englishName ?? "",
        description: row?.description ?? fallback.englishDescription ?? "",
      };
    } else if (locale === ZH_CN) {
      map[locale] = {
        name: row?.name ?? fallback.nonEnglishName ?? "",
        description: row?.description ?? fallback.nonEnglishDescription ?? "",
      };
    } else {
      map[locale] = {
        name: row?.name ?? "",
        description: row?.description ?? "",
      };
    }
  }
  return map;
}

export function buildTranslationsPayload(
  localeTranslations: LocaleTranslationMap | undefined,
  enabledLocales: string[]
): BusinessDataTranslation[] {
  if (!localeTranslations) {
    return [];
  }
  return enabledLocales
    .map((locale) => ({
      locale,
      name: localeTranslations[locale]?.name?.trim() || undefined,
      description: localeTranslations[locale]?.description?.trim() || undefined,
    }))
    .filter((entry) => entry.name || entry.description);
}

export function syncLegacyFieldsFromLocaleTranslations(
  localeTranslations: LocaleTranslationMap | undefined
): {
  englishName: string;
  nonEnglishName: string;
  englishDescription: string;
  nonEnglishDescription: string;
} {
  const en = localeTranslations?.[EN_US];
  const zh = localeTranslations?.[ZH_CN];
  return {
    englishName: en?.name?.trim() ?? "",
    nonEnglishName: zh?.name?.trim() ?? "",
    englishDescription: en?.description?.trim() ?? "",
    nonEnglishDescription: zh?.description?.trim() ?? "",
  };
}

export function syncLegacyNameFieldsFromLocaleTranslations(
  localeTranslations: LocaleTranslationMap | undefined
): {
  name: string;
  nonEnglishName: string;
} {
  const en = localeTranslations?.[EN_US];
  const zh = localeTranslations?.[ZH_CN];
  return {
    name: en?.name?.trim() ?? "",
    nonEnglishName: zh?.name?.trim() ?? "",
  };
}

/** Primary display name for cards/lists using translations + legacy fields. */
export function resolveLocalizedDisplayName(
  translations: BusinessDataTranslation[] | undefined,
  enabledLocales: string[],
  defaultLocale: string,
  fallback: {
    englishName?: string;
    nonEnglishName?: string;
  }
): string {
  const map = buildLocaleTranslationMap(translations, enabledLocales, fallback);
  const preferred = [defaultLocale, EN_US, ...enabledLocales];
  for (const locale of preferred) {
    const name = map[locale]?.name?.trim();
    if (name) {
      return name;
    }
  }
  return (fallback.englishName ?? fallback.nonEnglishName ?? "").trim();
}

/** Tooltip showing all locale names that have values. */
export function buildLocalizedNameTooltip(
  translations: BusinessDataTranslation[] | undefined,
  enabledLocales: string[],
  fallback: {
    englishName?: string;
    nonEnglishName?: string;
  },
  localeLabels?: Record<string, string>
): string {
  const map = buildLocaleTranslationMap(translations, enabledLocales, fallback);
  const parts = enabledLocales
    .map((locale) => {
      const name = map[locale]?.name?.trim();
      if (!name) {
        return null;
      }
      const label = localeLabels?.[locale] ?? locale;
      return `${label}: ${name}`;
    })
    .filter(Boolean);
  if (parts.length > 0) {
    return parts.join(" · ");
  }
  return [fallback.englishName, fallback.nonEnglishName].filter(Boolean).join(" · ");
}

export function localeLabel(localeCode: string, nativeLabel?: string, label?: string): string {
  if (nativeLabel && label && nativeLabel !== label) {
    return `${nativeLabel} (${label})`;
  }
  return nativeLabel || label || localeCode;
}

export function missingTranslationLocales(
  translations: BusinessDataTranslation[] | undefined,
  enabledLocales: string[]
): string[] {
  return enabledLocales.filter((locale) => {
    const row = translations?.find((entry) => entry.locale === locale);
    return !row?.name?.trim();
  });
}

export function isTranslationComplete(
  translations: BusinessDataTranslation[] | undefined,
  enabledLocales: string[]
): boolean {
  return missingTranslationLocales(translations, enabledLocales).length === 0;
}
